import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton, OrigenChip, RedChip, GreenChip, DangerButton, MainSectionPanel } from '../../../components/common';
import { EditDERC } from './components';
import { type DERCRequest } from './types/derc';
import { useDERCStore } from './store';

const DERCPage: React.FC = () => {
  const {
    dercs,
    loading,
    error,
    selectedDERC,
    dialogOpen,
    fetchDERCs,
    saveDERC,
    setSelectedDERC,
    openDialog,
    closeDialog,
    clearError
  } = useDERCStore();

  const [selectedMode, setSelectedMode] = useState<number | undefined>(undefined);

  const columns: GridColDef[] = [
    {
      field: 'numeroDocumento',
      headerName: 'N° DERC',
      width: 200,
    },
    {
      field: 'fecha',
      headerName: 'Extracción',
      width: 150,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'idOrigenProducto',
      headerName: 'Origen',
      width: 120,
      renderCell: (params) => (
        <OrigenChip idOrigenProducto={params.value} />
      ),
    },
    {
      field: 'numeroMallas',
      headerName: 'N° Mallas',
      width: 100,
      type: 'number'
    },
    {
      field: 'numeroManojos',
      headerName: 'N° Manojos',
      width: 110,
      type: 'number'
    },
    {
      field: 'placaVehiculo',
      headerName: 'Placa',
      width: 100
    },
    {
      field: 'asociacionDescripcion',
      headerName: 'Asociación',
      width: 200,
      flex: 1
    },
    {
      field: 'isCerrado',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        params.value ? (
          <RedChip label="Cerrado" />
        ) : (
          <GreenChip label="Abierto" />
        )
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <GridEditButton
            onClick={() => handleEdit(params.row.id)}
            tooltip="Editar"
            fontSize="small"
            ariaLabel={`Editar DERC ${params.row.numeroDocumento}`}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchDERCs();
  }, [fetchDERCs]);

  const handleAdd = (mode: number = 1) => {
    setSelectedDERC(null);
    setSelectedMode(mode);
    openDialog();
  };

  const handleEdit = (id: number) => {
    const derc = dercs.find((d) => d.id === id);
    setSelectedDERC(derc || null);
    setSelectedMode(undefined); // Reset mode when editing
    openDialog();
  };

  const handleSave = async (dercRequest: DERCRequest) => {
    try {
      await saveDERC(dercRequest);
    } catch (error) {
      console.error('Error saving DERC:', error);
    }
  };


  const handleCloseError = () => {
    clearError();
  };

  return (
    <MainSectionPanel>
      <PageTitle
        title="DERC"
        subtitle="Declaración de Entrega y Recepción de Carga"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <AddButton onClick={() => handleAdd(1)} text='Nuevo DERC' />
            <DangerButton onClick={() => handleAdd(2)}>
              Nuevo DERC B
            </DangerButton>
          </Box>
        }
      />

      <ListLayoutPanel>

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={dercs}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSize={25}
          />
        )}
      </ListLayoutPanel>

      <EditDERC
        open={dialogOpen}
        dercId={selectedDERC?.id || null}
        onClose={closeDialog}
        onSave={handleSave}
        defaultIdOrigenProducto={selectedMode}
      />
    </MainSectionPanel>
  );
};

export default DERCPage;